/**
 * Storage utilities for state persistence
 */

import SecureLS from "secure-ls"
import sha256 from "crypto-js/sha256"
import { CART_STORAGE_KEY } from '../constants'

let ls
let cartStateHash = ""

/**
 * Create content digest for state change detection
 * @param {string} content - Content to hash
 * @returns {string} SHA256 hash
 */
export function createContentDigest(content) {
  return sha256(content)
}

/**
 * Initialize storage
 * @returns {Object} Storage instance
 */
export function initializeStorage() {
  if (typeof window !== "undefined" && localStorage) {
    ls = new SecureLS({})
    return ls
  }
  return null
}

/**
 * Get stored cart data
 * @returns {string|null} Stored cart data
 */
export function getStoredCartData() {
  if (typeof window !== "undefined" && ls) {
    const cartData = ls.get(CART_STORAGE_KEY)
    cartStateHash = cartData
    return cartData
  }
  return null
}

/**
 * Clean state data before saving - remove sensitive/session data
 * @param {Object} state - State to clean
 * @returns {Object} Cleaned state
 */
export function cleanStateForStorage(state) {
  const cleanedState = { ...state }

  // Remove sensitive/session data that shouldn't be persisted
  const keysToRemove = [
    'ip',               // IP address (privacy concern)
    'radarID',          // Stripe Radar session ID (session-specific, keep in state but don't persist)
    'client',           // Shopify client (recreated fresh each time, keep in state but don't persist)
    'stripeForConfirmation',  // Stripe instance (contains circular references, recreated each session)
    'elementsForConfirmation', // Stripe Elements (contains circular references, recreated each session)
    // Note: stripe, os, so, soe are needed for functionality
  ]

  // Remove sensitive payment data
  if (cleanedState.pd) {
    cleanedState.pd = {
      // Only keep essential payment data, remove sensitive fields
      pi: cleanedState.pd.pi,
      // Remove cs (client secret) and other sensitive data
    }
  }

  // Clean up keys
  keysToRemove.forEach(key => {
    delete cleanedState[key]
  })

  return cleanedState
}

/**
 * Save state to storage
 * @param {Object} state - State to save
 */
export function saveState(state) {
  if (typeof window === "undefined" || !ls) return

  // Clean the state before saving
  const cleanedState = cleanStateForStorage(state)
  const stateString = JSON.stringify(cleanedState)
  const cartHash = createContentDigest(stateString)

  if (cartHash !== cartStateHash) {
    cartStateHash = cartHash
    ls.set(CART_STORAGE_KEY, stateString)
  }
}
